import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { TrendingUp, DollarSign, Users, Target } from "lucide-react";
import { Link } from "react-router-dom";

const PaidAdsCalculator = () => {
  const [monthlyBudget, setMonthlyBudget] = useState(2000);
  const [avgOrderValue, setAvgOrderValue] = useState(5000);
  const [conversionRate, setConversionRate] = useState(15);

  // Calculations based on industry averages and our performance data
  const avgCostPerClick = 3.5; // Average CPC
  const clickThroughRate = 0.045; // 4.5% CTR (industry above average)
  const leadConversionRate = 0.25; // 25% of clicks become leads
  
  const estimatedClicks = Math.floor(monthlyBudget / avgCostPerClick);
  const estimatedLeads = Math.floor(estimatedClicks * leadConversionRate);
  const estimatedConversions = Math.floor(estimatedLeads * (conversionRate / 100));
  const estimatedRevenue = estimatedConversions * avgOrderValue;
  const roi = ((estimatedRevenue - monthlyBudget) / monthlyBudget) * 100;
  const costPerLead = estimatedLeads > 0 ? Math.floor(monthlyBudget / estimatedLeads) : 0;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Input Section */}
        <Card className="p-8">
          <h3 className="text-2xl font-bold mb-6 text-foreground">Your Details</h3>
          
          <div className="space-y-6">
            {/* Monthly Budget */}
            <div>
              <Label htmlFor="budget" className="text-base font-semibold mb-2 block">
                Monthly Ad Budget
              </Label>
              <div className="flex items-center gap-4">
                <span className="text-2xl font-bold text-primary">£{monthlyBudget.toLocaleString()}</span>
              </div>
              <Slider
                id="budget"
                min={500}
                max={20000}
                step={500}
                value={[monthlyBudget]}
                onValueChange={(value) => setMonthlyBudget(value[0])}
                className="mt-4"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-2">
                <span>£500</span>
                <span>£20,000</span>
              </div>
            </div>

            {/* Average Order Value */}
            <div>
              <Label htmlFor="orderValue" className="text-base font-semibold mb-2 block">
                Average Client Value
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                <Input
                  id="orderValue"
                  type="number"
                  min="100"
                  step="100"
                  value={avgOrderValue}
                  onChange={(e) => setAvgOrderValue(Number(e.target.value))}
                  className="pl-8 text-lg"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Average revenue per customer/project
              </p>
            </div>

            {/* Conversion Rate */}
            <div>
              <Label htmlFor="conversion" className="text-base font-semibold mb-2 block">
                Lead-to-Customer Rate: {conversionRate}%
              </Label>
              <Slider
                id="conversion"
                min={5}
                max={50}
                step={5}
                value={[conversionRate]}
                onValueChange={(value) => setConversionRate(value[0])}
                className="mt-4"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-2">
                <span>5%</span>
                <span>50%</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                What % of leads become paying customers?
              </p>
            </div>
          </div>

          <div className="mt-8 p-4 bg-muted/50 rounded-lg">
            <p className="text-xs text-muted-foreground">
              <strong>Note:</strong> Calculations based on industry averages and our historical campaign performance. Actual results may vary based on your industry, offer, and market conditions.
            </p>
          </div>
        </Card>

        {/* Results Section */}
        <div className="space-y-6">
          <Card className="p-8 bg-gradient-to-br from-primary/5 to-secondary/5 border-2 border-primary/20">
            <h3 className="text-2xl font-bold mb-6 text-foreground">Estimated Monthly Results</h3>
            
            <div className="space-y-6">
              {/* ROI */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground mb-1">Return on Investment</p>
                  <p className="text-4xl font-bold text-primary">
                    {roi > 0 ? '+' : ''}{roi.toFixed(0)}%
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {roi > 0 
                      ? `You make £${((roi / 100) * monthlyBudget).toLocaleString()} for every £${monthlyBudget.toLocaleString()} spent`
                      : 'Increase your conversion rate or average order value to see positive ROI'
                    }
                  </p>
                </div>
              </div>

              {/* Estimated Revenue */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-6 h-6 text-secondary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground mb-1">Estimated Revenue</p>
                  <p className="text-4xl font-bold text-foreground">
                    £{estimatedRevenue.toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    From {estimatedConversions} new customer{estimatedConversions !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              {/* Estimated Leads */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-accent" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground mb-1">Qualified Leads</p>
                  <p className="text-4xl font-bold text-foreground">
                    {estimatedLeads}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    From approximately {estimatedClicks.toLocaleString()} clicks
                  </p>
                </div>
              </div>

              {/* Cost Per Lead */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground mb-1">Cost Per Lead</p>
                  <p className="text-4xl font-bold text-foreground">
                    £{costPerLead}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Average cost to acquire each qualified lead
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* CTA Card */}
          <Card className="p-6 bg-primary text-white">
            <h4 className="text-xl font-bold mb-2">Ready to see real results?</h4>
            <p className="text-white/90 text-sm mb-4">
              These numbers are conservative estimates. Our clients typically see better performance within 60-90 days of launch.
            </p>
            <Link
              to="/book-call"
              className="inline-block bg-white text-primary px-6 py-3 rounded-lg font-semibold hover:bg-white/90 transition-colors"
            >
              Book Your Free Strategy Call
            </Link>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PaidAdsCalculator;
